import { createContext, useContext, useState, useEffect, useMemo } from "react";

export const LANGUAGES = [
  { code: "en", label: "English", flag: "EN" },
  { code: "it", label: "Italiano", flag: "IT" },
  { code: "es", label: "Español", flag: "ES" },
  { code: "fr", label: "Français", flag: "FR" },
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "pt", label: "Português", flag: "PT" },
  { code: "nl", label: "Nederlands", flag: "NL" },
  { code: "pl", label: "Polski", flag: "PL" },
  { code: "tr", label: "Türkçe", flag: "TR" },
  { code: "ru", label: "Русский", flag: "RU" },
  { code: "uk", label: "Українська", flag: "UA" },
  { code: "ar", label: "العربية", flag: "AR" },
  { code: "hi", label: "हिन्दी", flag: "HI" },
  { code: "zh", label: "中文", flag: "ZH" },
  { code: "ja", label: "日本語", flag: "JA" },
  { code: "ko", label: "한국어", flag: "KO" },
  { code: "id", label: "Bahasa Indonesia", flag: "ID" },
  { code: "vi", label: "Tiếng Việt", flag: "VI" },
];

const RTL_LANGS = new Set(["ar"]);

const translations = {
  en: {
    brand: "Wholesale & Retail", tagline: "The B2B buy-request board.",
    subtagline: "Buyers post what they need. Sellers reply with quotes.",
    nav_home: "Board", nav_post: "Post a request",
    filter_all: "All categories", search_placeholder: "Search requests…",
    empty_title: "No requests yet", empty_desc: "Be the first to post a buy request. Suppliers will comment with quotes.",
    empty_cta: "Post the first request",
    request_by: "Posted by", quantity: "Quantity", budget: "Budget", deadline: "Deadline", category: "Category",
    view_details: "View & offer", back_to_board: "Back to board", offers_count: "seller offers",
    no_offers: "No offers yet — be the first supplier to reply.", add_offer: "Add your offer",
    form_new_request: "New buy request",
    form_new_request_desc: "Describe what you want to buy. Suppliers will comment with prices and availability.",
    title_label: "Request title", title_placeholder: "e.g. Red pencils, production run of 80,000",
    description_label: "Description", description_placeholder: "Specs, materials, quality requirements…",
    category_label: "Category", category_placeholder: "Select a category",
    quantity_label: "Quantity", budget_label: "Budget", budget_placeholder: "e.g. $500 or open",
    deadline_label: "Needed by", buyer_name_label: "Your name / company", buyer_name_placeholder: "e.g. Acme Studio",
    submit_request: "Publish request",
    seller_name: "Company / your name", seller_name_placeholder: "e.g. Pencils R Us",
    price_label: "Price offered", price_placeholder: "e.g. $450 for 10,000 units",
    availability_label: "Availability", availability_placeholder: "e.g. In stock, ships in 2 days",
    offer_description_label: "Details", offer_description_placeholder: "Describe the product, MOQ, terms…",
    contact_label: "How to reach you", contact_placeholder: "email or phone",
    submit_offer: "Submit offer", uploading: "Uploading…", posting: "Publishing…",
    posted: "Request published", offer_posted: "Offer submitted",
    error_upload: "Upload failed", error_submit: "Something went wrong",
    hero_kicker: "Buy-request board", hero_line_1: "Post what you need.", hero_line_2: "Let suppliers come to you.",
    stat_requests: "open requests", stat_offers: "seller offers", filters: "Filters", live: "Live",
    media_add: "Click or drag photos & videos",
    media_hint: "Multiple files · Images (max 10MB) · Videos (max 100MB)",
    photo_label: "Photos & videos (optional)",
    email_label: "Email", email_placeholder: "you@company.com",
    phone_label: "Phone", phone_placeholder: "+1 555 000 0000",
    contact_tab: "Contact", details_tab: "Details", offers_tab: "Offers", ratings_tab: "Ratings",
    leave_rating: "Leave a rating", your_name: "Your name", your_name_placeholder: "e.g. Marco B.",
    comment_optional: "Comment (optional)", comment_placeholder: "Share your experience…",
    submit_rating: "Submit rating", rating_posted: "Rating submitted",
    reviews: "reviews", no_ratings: "No ratings yet — be the first.",
    language: "Language",
    // v3 additions
    tab_requests: "Buy Requests", tab_products: "Products",
    nav_post_product: "List a product",
    hero_products_kicker: "Product catalog",
    hero_products_1: "Stock the market.",
    hero_products_2: "List wholesale or retail products for buyers to discover.",
    stat_products: "listed products",
    products_empty_title: "No products listed yet",
    products_empty_desc: "Be the first seller to list a product for wholesale or retail buyers.",
    products_empty_cta: "List the first product",
    filter_all_sale: "Wholesale & Retail", filter_wholesale: "Wholesale", filter_retail: "Retail",
    form_new_product: "List a new product",
    form_new_product_desc: "Add a product to your catalog. Buyers can browse and contact you directly.",
    product_title_placeholder: "e.g. Handcrafted leather notebooks",
    price_per_unit: "Price",
    price_per_unit_placeholder: "e.g. $12 / unit or $2 wholesale",
    moq_label: "Min. order (optional)",
    moq_placeholder: "e.g. 100 units",
    stock_label: "Stock (optional)",
    stock_placeholder: "e.g. 5000 in stock",
    sale_type_label: "Sales type",
    sale_type_wholesale: "Wholesale only",
    sale_type_retail: "Retail only",
    sale_type_both: "Wholesale & Retail",
    submit_product: "Publish product",
    product_posted: "Product listed",
    seller_profile: "Seller profile",
    joined: "Active since",
    total_products: "products",
    total_offers: "quotes given",
    view_seller: "View seller",
    contact_seller: "Contact seller",
    no_products_seller: "This seller has not listed any products yet.",
    no_offers_seller: "This seller has not answered any buy requests yet.",
    catalog: "Catalog",
    quotes_given: "Quotes given",
    rate_seller: "Rate this seller",
  },
  it: {
    brand: "Wholesale & Retail", tagline: "Bacheca B2B di richieste d'acquisto.",
    subtagline: "I compratori pubblicano ciò di cui hanno bisogno. I venditori rispondono con preventivi.",
    nav_home: "Bacheca", nav_post: "Pubblica richiesta",
    filter_all: "Tutte le categorie", search_placeholder: "Cerca richieste…",
    empty_title: "Nessuna richiesta ancora", empty_desc: "Sii il primo a pubblicare una richiesta. I fornitori commenteranno con preventivi.",
    empty_cta: "Pubblica la prima richiesta",
    request_by: "Pubblicato da", quantity: "Quantità", budget: "Budget", deadline: "Scadenza", category: "Categoria",
    view_details: "Vedi e offri", back_to_board: "Torna alla bacheca", offers_count: "offerte venditori",
    no_offers: "Nessuna offerta ancora — sii il primo fornitore.", add_offer: "Aggiungi offerta",
    form_new_request: "Nuova richiesta d'acquisto",
    form_new_request_desc: "Descrivi cosa vuoi comprare. I fornitori commenteranno con prezzi e disponibilità.",
    title_label: "Titolo richiesta", title_placeholder: "es. Matite rosse, produzione 80.000",
    description_label: "Descrizione", description_placeholder: "Specifiche, materiali, requisiti…",
    category_label: "Categoria", category_placeholder: "Seleziona categoria",
    quantity_label: "Quantità", budget_label: "Budget", budget_placeholder: "es. €500 o aperto",
    deadline_label: "Necessario entro", buyer_name_label: "Nome / azienda", buyer_name_placeholder: "es. Acme Studio",
    submit_request: "Pubblica richiesta",
    seller_name: "Azienda / il tuo nome", seller_name_placeholder: "es. Pencils R Us",
    price_label: "Prezzo offerto", price_placeholder: "es. €450 per 10.000 pezzi",
    availability_label: "Disponibilità", availability_placeholder: "es. In magazzino, spedizione 2 giorni",
    offer_description_label: "Dettagli", offer_description_placeholder: "Descrivi il prodotto, MOQ, condizioni…",
    contact_label: "Come contattarti", contact_placeholder: "email o telefono",
    submit_offer: "Invia offerta", uploading: "Caricamento…", posting: "Pubblicazione…",
    posted: "Richiesta pubblicata", offer_posted: "Offerta inviata",
    error_upload: "Caricamento fallito", error_submit: "Qualcosa è andato storto",
    hero_kicker: "Bacheca richieste", hero_line_1: "Pubblica ciò che ti serve.", hero_line_2: "Lascia che i fornitori vengano da te.",
    stat_requests: "richieste aperte", stat_offers: "offerte venditori", filters: "Filtri", live: "Live",
    media_add: "Clicca o trascina foto e video",
    media_hint: "File multipli · Immagini (max 10MB) · Video (max 100MB)",
    photo_label: "Foto e video (opzionale)",
    email_label: "Email", email_placeholder: "tu@azienda.com",
    phone_label: "Telefono", phone_placeholder: "+39 333 000 0000",
    contact_tab: "Contatti", details_tab: "Dettagli", offers_tab: "Offerte", ratings_tab: "Valutazioni",
    leave_rating: "Lascia una valutazione", your_name: "Il tuo nome", your_name_placeholder: "es. Marco B.",
    comment_optional: "Commento (opzionale)", comment_placeholder: "Condividi la tua esperienza…",
    submit_rating: "Invia valutazione", rating_posted: "Valutazione inviata",
    reviews: "recensioni", no_ratings: "Nessuna valutazione — sii il primo.",
    language: "Lingua",
  },
  es: {
    brand: "Wholesale & Retail", tagline: "Tablero B2B de solicitudes de compra.",
    subtagline: "Los compradores publican lo que necesitan. Los vendedores responden con cotizaciones.",
    nav_home: "Tablero", nav_post: "Publicar solicitud",
    filter_all: "Todas las categorías", search_placeholder: "Buscar solicitudes…",
    empty_title: "Aún no hay solicitudes", empty_desc: "Sé el primero en publicar. Los proveedores comentarán con cotizaciones.",
    empty_cta: "Publicar la primera",
    request_by: "Publicado por", quantity: "Cantidad", budget: "Presupuesto", deadline: "Fecha límite", category: "Categoría",
    view_details: "Ver y ofertar", back_to_board: "Volver al tablero", offers_count: "ofertas de vendedores",
    no_offers: "Aún sin ofertas — sé el primer proveedor.", add_offer: "Agregar oferta",
    form_new_request: "Nueva solicitud de compra",
    form_new_request_desc: "Describe qué quieres comprar. Los proveedores comentarán con precios y disponibilidad.",
    title_label: "Título de solicitud", title_placeholder: "ej. Lápices rojos, producción 80.000",
    description_label: "Descripción", description_placeholder: "Especificaciones, materiales, calidad…",
    category_label: "Categoría", category_placeholder: "Seleccionar categoría",
    quantity_label: "Cantidad", budget_label: "Presupuesto", budget_placeholder: "ej. $500 o abierto",
    deadline_label: "Necesario para", buyer_name_label: "Tu nombre / empresa", buyer_name_placeholder: "ej. Acme Studio",
    submit_request: "Publicar solicitud",
    seller_name: "Empresa / tu nombre", seller_name_placeholder: "ej. Pencils R Us",
    price_label: "Precio ofrecido", price_placeholder: "ej. $450 por 10.000 unidades",
    availability_label: "Disponibilidad", availability_placeholder: "ej. En stock, envío en 2 días",
    offer_description_label: "Detalles", offer_description_placeholder: "Producto, MOQ, términos…",
    contact_label: "Cómo contactarte", contact_placeholder: "email o teléfono",
    submit_offer: "Enviar oferta", uploading: "Subiendo…", posting: "Publicando…",
    posted: "Solicitud publicada", offer_posted: "Oferta enviada",
    error_upload: "Error al subir", error_submit: "Algo salió mal",
    hero_kicker: "Tablero de solicitudes", hero_line_1: "Publica lo que necesitas.", hero_line_2: "Deja que los proveedores vengan a ti.",
    stat_requests: "solicitudes abiertas", stat_offers: "ofertas", filters: "Filtros", live: "En vivo",
    media_add: "Haz clic o arrastra fotos y videos",
    media_hint: "Múltiples archivos · Imágenes (máx 10MB) · Videos (máx 100MB)",
    photo_label: "Fotos y videos (opcional)",
    email_label: "Email", email_placeholder: "tu@empresa.com",
    phone_label: "Teléfono", phone_placeholder: "+34 600 000 000",
    contact_tab: "Contacto", details_tab: "Detalles", offers_tab: "Ofertas", ratings_tab: "Valoraciones",
    leave_rating: "Dejar una valoración", your_name: "Tu nombre", your_name_placeholder: "ej. María G.",
    comment_optional: "Comentario (opcional)", comment_placeholder: "Comparte tu experiencia…",
    submit_rating: "Enviar valoración", rating_posted: "Valoración enviada",
    reviews: "reseñas", no_ratings: "Sin valoraciones — sé el primero.",
    language: "Idioma",
  },
  fr: {
    brand: "Wholesale & Retail", tagline: "Tableau B2B de demandes d'achat.",
    subtagline: "Les acheteurs publient leurs besoins. Les vendeurs répondent avec des devis.",
    nav_home: "Tableau", nav_post: "Publier une demande",
    filter_all: "Toutes catégories", search_placeholder: "Rechercher…",
    empty_title: "Aucune demande", empty_desc: "Soyez le premier à publier. Les fournisseurs répondront avec des devis.",
    empty_cta: "Publier la première",
    request_by: "Publié par", quantity: "Quantité", budget: "Budget", deadline: "Échéance", category: "Catégorie",
    view_details: "Voir et offrir", back_to_board: "Retour au tableau", offers_count: "offres vendeurs",
    no_offers: "Pas encore d'offres — soyez le premier.", add_offer: "Ajouter une offre",
    form_new_request: "Nouvelle demande d'achat",
    form_new_request_desc: "Décrivez ce que vous voulez acheter. Les fournisseurs répondront avec prix et disponibilité.",
    title_label: "Titre", title_placeholder: "ex. Crayons rouges, production 80 000",
    description_label: "Description", description_placeholder: "Spécifications, matériaux, qualité…",
    category_label: "Catégorie", category_placeholder: "Sélectionner",
    quantity_label: "Quantité", budget_label: "Budget", budget_placeholder: "ex. 500 € ou ouvert",
    deadline_label: "Requis pour", buyer_name_label: "Votre nom / société", buyer_name_placeholder: "ex. Acme Studio",
    submit_request: "Publier",
    seller_name: "Société / votre nom", seller_name_placeholder: "ex. Pencils R Us",
    price_label: "Prix offert", price_placeholder: "ex. 450 € pour 10 000 unités",
    availability_label: "Disponibilité", availability_placeholder: "ex. En stock, expédition 2 jours",
    offer_description_label: "Détails", offer_description_placeholder: "Produit, MOQ, conditions…",
    contact_label: "Comment vous joindre", contact_placeholder: "email ou téléphone",
    submit_offer: "Envoyer l'offre", uploading: "Téléversement…", posting: "Publication…",
    posted: "Demande publiée", offer_posted: "Offre envoyée",
    error_upload: "Téléversement échoué", error_submit: "Une erreur s'est produite",
    hero_kicker: "Tableau demandes", hero_line_1: "Publiez vos besoins.", hero_line_2: "Laissez les fournisseurs venir à vous.",
    stat_requests: "demandes ouvertes", stat_offers: "offres", filters: "Filtres", live: "En direct",
    media_add: "Cliquez ou glissez photos et vidéos",
    media_hint: "Plusieurs fichiers · Images (max 10 Mo) · Vidéos (max 100 Mo)",
    photo_label: "Photos et vidéos (facultatif)",
    email_label: "Email", email_placeholder: "vous@société.com",
    phone_label: "Téléphone", phone_placeholder: "+33 6 00 00 00 00",
    contact_tab: "Contact", details_tab: "Détails", offers_tab: "Offres", ratings_tab: "Notes",
    leave_rating: "Laisser une note", your_name: "Votre nom", your_name_placeholder: "ex. Marie D.",
    comment_optional: "Commentaire (facultatif)", comment_placeholder: "Partagez votre expérience…",
    submit_rating: "Envoyer la note", rating_posted: "Note envoyée",
    reviews: "avis", no_ratings: "Aucune note — soyez le premier.",
    language: "Langue",
  },
  de: {
    brand: "Wholesale & Retail", tagline: "B2B-Einkaufsanfragen-Board.",
    subtagline: "Käufer posten Bedarf. Verkäufer antworten mit Angeboten.",
    nav_home: "Board", nav_post: "Anfrage stellen",
    filter_all: "Alle Kategorien", search_placeholder: "Anfragen suchen…",
    empty_title: "Noch keine Anfragen", empty_desc: "Sei der Erste. Lieferanten antworten mit Angeboten.",
    empty_cta: "Erste Anfrage posten",
    request_by: "Gepostet von", quantity: "Menge", budget: "Budget", deadline: "Frist", category: "Kategorie",
    view_details: "Ansehen & anbieten", back_to_board: "Zurück zum Board", offers_count: "Verkäufer-Angebote",
    no_offers: "Noch keine Angebote — sei der erste Lieferant.", add_offer: "Angebot abgeben",
    form_new_request: "Neue Einkaufsanfrage",
    form_new_request_desc: "Beschreibe was du kaufen willst. Lieferanten antworten mit Preis und Verfügbarkeit.",
    title_label: "Titel", title_placeholder: "z. B. Rote Bleistifte, Produktion 80.000",
    description_label: "Beschreibung", description_placeholder: "Spezifikationen, Materialien, Qualität…",
    category_label: "Kategorie", category_placeholder: "Kategorie auswählen",
    quantity_label: "Menge", budget_label: "Budget", budget_placeholder: "z. B. 500 € oder offen",
    deadline_label: "Benötigt bis", buyer_name_label: "Dein Name / Firma", buyer_name_placeholder: "z. B. Acme Studio",
    submit_request: "Anfrage veröffentlichen",
    seller_name: "Firma / dein Name", seller_name_placeholder: "z. B. Pencils R Us",
    price_label: "Angebotener Preis", price_placeholder: "z. B. 450 € für 10.000 Stück",
    availability_label: "Verfügbarkeit", availability_placeholder: "z. B. Auf Lager, Versand 2 Tage",
    offer_description_label: "Details", offer_description_placeholder: "Produkt, MOQ, Bedingungen…",
    contact_label: "Wie erreichbar", contact_placeholder: "E-Mail oder Telefon",
    submit_offer: "Angebot senden", uploading: "Hochladen…", posting: "Veröffentlichen…",
    posted: "Anfrage veröffentlicht", offer_posted: "Angebot gesendet",
    error_upload: "Upload fehlgeschlagen", error_submit: "Etwas ist schiefgelaufen",
    hero_kicker: "Anfrage-Board", hero_line_1: "Poste, was du brauchst.", hero_line_2: "Lass Lieferanten zu dir kommen.",
    stat_requests: "offene Anfragen", stat_offers: "Angebote", filters: "Filter", live: "Live",
    media_add: "Klicken oder Fotos & Videos ziehen",
    media_hint: "Mehrere Dateien · Bilder (max 10 MB) · Videos (max 100 MB)",
    photo_label: "Fotos und Videos (optional)",
    email_label: "E-Mail", email_placeholder: "du@firma.de",
    phone_label: "Telefon", phone_placeholder: "+49 30 000000",
    contact_tab: "Kontakt", details_tab: "Details", offers_tab: "Angebote", ratings_tab: "Bewertungen",
    leave_rating: "Bewertung abgeben", your_name: "Dein Name", your_name_placeholder: "z. B. Anna M.",
    comment_optional: "Kommentar (optional)", comment_placeholder: "Teile deine Erfahrung…",
    submit_rating: "Bewertung senden", rating_posted: "Bewertung gesendet",
    reviews: "Bewertungen", no_ratings: "Noch keine Bewertungen — sei der Erste.",
    language: "Sprache",
  },
  pt: {
    brand: "Wholesale & Retail", tagline: "Quadro B2B de pedidos de compra.",
    subtagline: "Compradores postam o que precisam. Vendedores respondem com cotações.",
    nav_home: "Quadro", nav_post: "Publicar pedido",
    filter_all: "Todas as categorias", search_placeholder: "Buscar pedidos…",
    empty_title: "Sem pedidos ainda", empty_desc: "Seja o primeiro. Fornecedores responderão com cotações.",
    empty_cta: "Publicar o primeiro",
    request_by: "Publicado por", quantity: "Quantidade", budget: "Orçamento", deadline: "Prazo", category: "Categoria",
    view_details: "Ver e ofertar", back_to_board: "Voltar ao quadro", offers_count: "ofertas de vendedores",
    no_offers: "Sem ofertas ainda — seja o primeiro fornecedor.", add_offer: "Adicionar oferta",
    form_new_request: "Novo pedido de compra",
    form_new_request_desc: "Descreva o que quer comprar. Fornecedores responderão com preços e disponibilidade.",
    title_label: "Título", title_placeholder: "ex. Lápis vermelhos, produção 80.000",
    description_label: "Descrição", description_placeholder: "Especificações, materiais, qualidade…",
    category_label: "Categoria", category_placeholder: "Selecionar categoria",
    quantity_label: "Quantidade", budget_label: "Orçamento", budget_placeholder: "ex. R$500 ou aberto",
    deadline_label: "Precisa até", buyer_name_label: "Seu nome / empresa", buyer_name_placeholder: "ex. Acme Studio",
    submit_request: "Publicar pedido",
    seller_name: "Empresa / seu nome", seller_name_placeholder: "ex. Pencils R Us",
    price_label: "Preço oferecido", price_placeholder: "ex. R$450 por 10.000 unidades",
    availability_label: "Disponibilidade", availability_placeholder: "ex. Em estoque, envio em 2 dias",
    offer_description_label: "Detalhes", offer_description_placeholder: "Produto, MOQ, condições…",
    contact_label: "Como te contatar", contact_placeholder: "email ou telefone",
    submit_offer: "Enviar oferta", uploading: "Enviando…", posting: "Publicando…",
    posted: "Pedido publicado", offer_posted: "Oferta enviada",
    error_upload: "Falha no upload", error_submit: "Algo deu errado",
    hero_kicker: "Quadro de pedidos", hero_line_1: "Poste o que precisa.", hero_line_2: "Deixe os fornecedores virem até você.",
    stat_requests: "pedidos abertos", stat_offers: "ofertas", filters: "Filtros", live: "Ao vivo",
    media_add: "Clique ou arraste fotos e vídeos",
    media_hint: "Vários arquivos · Imagens (max 10MB) · Vídeos (max 100MB)",
    photo_label: "Fotos e vídeos (opcional)",
    email_label: "Email", email_placeholder: "voce@empresa.com",
    phone_label: "Telefone", phone_placeholder: "+55 11 90000 0000",
    contact_tab: "Contato", details_tab: "Detalhes", offers_tab: "Ofertas", ratings_tab: "Avaliações",
    leave_rating: "Deixar avaliação", your_name: "Seu nome", your_name_placeholder: "ex. João P.",
    comment_optional: "Comentário (opcional)", comment_placeholder: "Compartilhe sua experiência…",
    submit_rating: "Enviar avaliação", rating_posted: "Avaliação enviada",
    reviews: "avaliações", no_ratings: "Sem avaliações — seja o primeiro.",
    language: "Idioma",
  },
  nl: {
    brand: "Wholesale & Retail", tagline: "B2B-inkoopaanvragenbord.",
    subtagline: "Kopers plaatsen behoeften. Verkopers reageren met offertes.",
    nav_home: "Bord", nav_post: "Aanvraag plaatsen",
    filter_all: "Alle categorieën", search_placeholder: "Zoek aanvragen…",
    empty_title: "Nog geen aanvragen", empty_desc: "Wees de eerste. Leveranciers reageren met offertes.",
    empty_cta: "Plaats de eerste",
    request_by: "Geplaatst door", quantity: "Aantal", budget: "Budget", deadline: "Deadline", category: "Categorie",
    view_details: "Bekijk & bied", back_to_board: "Terug", offers_count: "aanbiedingen",
    no_offers: "Nog geen aanbiedingen — wees de eerste.", add_offer: "Aanbieding toevoegen",
    form_new_request: "Nieuwe inkoopaanvraag",
    form_new_request_desc: "Beschrijf wat je wil kopen. Leveranciers reageren met prijs en beschikbaarheid.",
    title_label: "Titel", title_placeholder: "bv. Rode potloden, productie 80.000",
    description_label: "Beschrijving", description_placeholder: "Specificaties, materialen, kwaliteit…",
    category_label: "Categorie", category_placeholder: "Kies categorie",
    quantity_label: "Aantal", budget_label: "Budget", budget_placeholder: "bv. €500 of open",
    deadline_label: "Nodig voor", buyer_name_label: "Naam / bedrijf", buyer_name_placeholder: "bv. Acme Studio",
    submit_request: "Aanvraag plaatsen",
    seller_name: "Bedrijf / je naam", seller_name_placeholder: "bv. Pencils R Us",
    price_label: "Aangeboden prijs", price_placeholder: "bv. €450 voor 10.000 stuks",
    availability_label: "Beschikbaarheid", availability_placeholder: "bv. Op voorraad, verzending 2 dagen",
    offer_description_label: "Details", offer_description_placeholder: "Product, MOQ, voorwaarden…",
    contact_label: "Contact", contact_placeholder: "email of telefoon",
    submit_offer: "Aanbieding versturen", uploading: "Uploaden…", posting: "Plaatsen…",
    posted: "Aanvraag geplaatst", offer_posted: "Aanbieding verstuurd",
    error_upload: "Upload mislukt", error_submit: "Er ging iets mis",
    hero_kicker: "Aanvragenbord", hero_line_1: "Plaats wat je nodig hebt.", hero_line_2: "Laat leveranciers naar jou komen.",
    stat_requests: "open aanvragen", stat_offers: "aanbiedingen", filters: "Filters", live: "Live",
    media_add: "Klik of sleep foto's en video's",
    media_hint: "Meerdere bestanden · Afbeeldingen (max 10MB) · Video's (max 100MB)",
    photo_label: "Foto's en video's (optioneel)",
    email_label: "Email", email_placeholder: "jij@bedrijf.nl",
    phone_label: "Telefoon", phone_placeholder: "+31 6 00000000",
    contact_tab: "Contact", details_tab: "Details", offers_tab: "Aanbiedingen", ratings_tab: "Beoordelingen",
    leave_rating: "Laat een beoordeling achter", your_name: "Je naam", your_name_placeholder: "bv. Piet V.",
    comment_optional: "Opmerking (optioneel)", comment_placeholder: "Deel je ervaring…",
    submit_rating: "Beoordeling versturen", rating_posted: "Beoordeling verstuurd",
    reviews: "beoordelingen", no_ratings: "Nog geen beoordelingen — wees de eerste.",
    language: "Taal",
  },
  pl: {
    brand: "Wholesale & Retail", tagline: "Tablica zapytań zakupowych B2B.",
    subtagline: "Kupujący publikują potrzeby. Sprzedawcy odpowiadają ofertami.",
    nav_home: "Tablica", nav_post: "Dodaj zapytanie",
    filter_all: "Wszystkie kategorie", search_placeholder: "Szukaj zapytań…",
    empty_title: "Brak zapytań", empty_desc: "Bądź pierwszy. Dostawcy odpowiedzą ofertami.",
    empty_cta: "Dodaj pierwsze",
    request_by: "Opublikowane przez", quantity: "Ilość", budget: "Budżet", deadline: "Termin", category: "Kategoria",
    view_details: "Zobacz i zaoferuj", back_to_board: "Powrót", offers_count: "ofert sprzedawców",
    no_offers: "Brak ofert — bądź pierwszy.", add_offer: "Dodaj ofertę",
    form_new_request: "Nowe zapytanie zakupowe",
    form_new_request_desc: "Opisz co chcesz kupić. Dostawcy odpowiedzą cenami i dostępnością.",
    title_label: "Tytuł", title_placeholder: "np. Czerwone ołówki, produkcja 80 000",
    description_label: "Opis", description_placeholder: "Specyfikacja, materiały, jakość…",
    category_label: "Kategoria", category_placeholder: "Wybierz kategorię",
    quantity_label: "Ilość", budget_label: "Budżet", budget_placeholder: "np. 500 zł lub otwarte",
    deadline_label: "Potrzebne do", buyer_name_label: "Imię / firma", buyer_name_placeholder: "np. Acme Studio",
    submit_request: "Opublikuj",
    seller_name: "Firma / imię", seller_name_placeholder: "np. Pencils R Us",
    price_label: "Oferowana cena", price_placeholder: "np. 450 zł za 10 000 sztuk",
    availability_label: "Dostępność", availability_placeholder: "np. W magazynie, wysyłka 2 dni",
    offer_description_label: "Szczegóły", offer_description_placeholder: "Produkt, MOQ, warunki…",
    contact_label: "Jak się skontaktować", contact_placeholder: "email lub telefon",
    submit_offer: "Wyślij ofertę", uploading: "Przesyłanie…", posting: "Publikowanie…",
    posted: "Zapytanie opublikowane", offer_posted: "Oferta wysłana",
    error_upload: "Błąd przesyłania", error_submit: "Coś poszło nie tak",
    hero_kicker: "Tablica zapytań", hero_line_1: "Opublikuj czego potrzebujesz.", hero_line_2: "Niech dostawcy przyjdą do Ciebie.",
    stat_requests: "otwarte zapytania", stat_offers: "oferty", filters: "Filtry", live: "Na żywo",
    media_add: "Kliknij lub przeciągnij zdjęcia i wideo",
    media_hint: "Wiele plików · Obrazy (max 10MB) · Wideo (max 100MB)",
    photo_label: "Zdjęcia i wideo (opcjonalnie)",
    email_label: "Email", email_placeholder: "ty@firma.pl",
    phone_label: "Telefon", phone_placeholder: "+48 500 000 000",
    contact_tab: "Kontakt", details_tab: "Szczegóły", offers_tab: "Oferty", ratings_tab: "Oceny",
    leave_rating: "Zostaw ocenę", your_name: "Twoje imię", your_name_placeholder: "np. Anna K.",
    comment_optional: "Komentarz (opcjonalny)", comment_placeholder: "Podziel się doświadczeniem…",
    submit_rating: "Wyślij ocenę", rating_posted: "Ocena wysłana",
    reviews: "recenzje", no_ratings: "Brak ocen — bądź pierwszy.",
    language: "Język",
  },
  tr: {
    brand: "Wholesale & Retail", tagline: "B2B alım talepleri panosu.",
    subtagline: "Alıcılar ihtiyaçlarını paylaşır. Satıcılar teklifle cevaplar.",
    nav_home: "Pano", nav_post: "Talep oluştur",
    filter_all: "Tüm kategoriler", search_placeholder: "Talepleri ara…",
    empty_title: "Henüz talep yok", empty_desc: "İlk sen paylaş. Tedarikçiler teklif verecek.",
    empty_cta: "İlk talebi oluştur",
    request_by: "Yayınlayan", quantity: "Miktar", budget: "Bütçe", deadline: "Son tarih", category: "Kategori",
    view_details: "Görüntüle ve teklif ver", back_to_board: "Panoya dön", offers_count: "satıcı teklifi",
    no_offers: "Henüz teklif yok — ilk sen yaz.", add_offer: "Teklif ekle",
    form_new_request: "Yeni alım talebi",
    form_new_request_desc: "Ne almak istediğini anlat. Tedarikçiler fiyat ve stok bilgisiyle cevaplar.",
    title_label: "Başlık", title_placeholder: "örn. Kırmızı kalem, üretim 80.000",
    description_label: "Açıklama", description_placeholder: "Özellikler, malzeme, kalite…",
    category_label: "Kategori", category_placeholder: "Kategori seç",
    quantity_label: "Miktar", budget_label: "Bütçe", budget_placeholder: "örn. 500₺ veya açık",
    deadline_label: "Ne zamana kadar", buyer_name_label: "Adın / şirket", buyer_name_placeholder: "örn. Acme Studio",
    submit_request: "Yayınla",
    seller_name: "Şirket / adın", seller_name_placeholder: "örn. Pencils R Us",
    price_label: "Teklif fiyatı", price_placeholder: "örn. 10.000 adet için 450₺",
    availability_label: "Stok durumu", availability_placeholder: "örn. Stokta, 2 gün içinde",
    offer_description_label: "Detay", offer_description_placeholder: "Ürün, minimum sipariş, şartlar…",
    contact_label: "İletişim", contact_placeholder: "email veya telefon",
    submit_offer: "Teklifi gönder", uploading: "Yükleniyor…", posting: "Yayınlanıyor…",
    posted: "Talep yayınlandı", offer_posted: "Teklif gönderildi",
    error_upload: "Yükleme başarısız", error_submit: "Bir şey ters gitti",
    hero_kicker: "Talep panosu", hero_line_1: "İhtiyacını paylaş.", hero_line_2: "Bırak tedarikçiler sana gelsin.",
    stat_requests: "açık talep", stat_offers: "teklif", filters: "Filtreler", live: "Canlı",
    media_add: "Fotoğraf ve videoları tıkla veya sürükle",
    media_hint: "Birden fazla dosya · Görsel (max 10MB) · Video (max 100MB)",
    photo_label: "Fotoğraf ve video (isteğe bağlı)",
    email_label: "Email", email_placeholder: "sen@sirket.com",
    phone_label: "Telefon", phone_placeholder: "+90 555 000 0000",
    contact_tab: "İletişim", details_tab: "Detay", offers_tab: "Teklifler", ratings_tab: "Değerlendirmeler",
    leave_rating: "Değerlendir", your_name: "Adın", your_name_placeholder: "örn. Ahmet Y.",
    comment_optional: "Yorum (isteğe bağlı)", comment_placeholder: "Deneyimini paylaş…",
    submit_rating: "Değerlendirmeyi gönder", rating_posted: "Değerlendirme gönderildi",
    reviews: "değerlendirme", no_ratings: "Değerlendirme yok — ilk sen ol.",
    language: "Dil",
  },
  ru: {
    brand: "Wholesale & Retail", tagline: "B2B доска заявок на закупку.",
    subtagline: "Покупатели публикуют потребности. Продавцы отвечают предложениями.",
    nav_home: "Доска", nav_post: "Опубликовать заявку",
    filter_all: "Все категории", search_placeholder: "Поиск заявок…",
    empty_title: "Заявок пока нет", empty_desc: "Будьте первым. Поставщики ответят предложениями.",
    empty_cta: "Опубликовать первую",
    request_by: "Опубликовал", quantity: "Количество", budget: "Бюджет", deadline: "Срок", category: "Категория",
    view_details: "Смотреть и предложить", back_to_board: "К доске", offers_count: "предложений продавцов",
    no_offers: "Пока нет предложений — будьте первым.", add_offer: "Добавить предложение",
    form_new_request: "Новая заявка на закупку",
    form_new_request_desc: "Опишите, что хотите купить. Поставщики ответят с ценой и наличием.",
    title_label: "Название", title_placeholder: "напр. Красные карандаши, производство 80 000",
    description_label: "Описание", description_placeholder: "Спецификации, материалы, качество…",
    category_label: "Категория", category_placeholder: "Выбрать категорию",
    quantity_label: "Количество", budget_label: "Бюджет", budget_placeholder: "напр. 500$ или открытый",
    deadline_label: "Нужно к", buyer_name_label: "Ваше имя / компания", buyer_name_placeholder: "напр. Acme Studio",
    submit_request: "Опубликовать",
    seller_name: "Компания / имя", seller_name_placeholder: "напр. Pencils R Us",
    price_label: "Цена", price_placeholder: "напр. 450$ за 10 000 шт.",
    availability_label: "Наличие", availability_placeholder: "напр. В наличии, отгрузка 2 дня",
    offer_description_label: "Детали", offer_description_placeholder: "Продукт, MOQ, условия…",
    contact_label: "Как связаться", contact_placeholder: "email или телефон",
    submit_offer: "Отправить предложение", uploading: "Загрузка…", posting: "Публикация…",
    posted: "Заявка опубликована", offer_posted: "Предложение отправлено",
    error_upload: "Ошибка загрузки", error_submit: "Что-то пошло не так",
    hero_kicker: "Доска заявок", hero_line_1: "Опубликуйте что вам нужно.", hero_line_2: "Пусть поставщики придут к вам.",
    stat_requests: "открытых заявок", stat_offers: "предложений", filters: "Фильтры", live: "В прямом эфире",
    media_add: "Кликните или перетащите фото и видео",
    media_hint: "Несколько файлов · Изображения (макс 10МБ) · Видео (макс 100МБ)",
    photo_label: "Фото и видео (опционально)",
    email_label: "Email", email_placeholder: "вы@компания.ру",
    phone_label: "Телефон", phone_placeholder: "+7 900 000 00 00",
    contact_tab: "Контакт", details_tab: "Детали", offers_tab: "Предложения", ratings_tab: "Оценки",
    leave_rating: "Оставить оценку", your_name: "Ваше имя", your_name_placeholder: "напр. Иван П.",
    comment_optional: "Комментарий (опционально)", comment_placeholder: "Поделитесь опытом…",
    submit_rating: "Отправить оценку", rating_posted: "Оценка отправлена",
    reviews: "отзывы", no_ratings: "Пока нет оценок — будьте первым.",
    language: "Язык",
  },
  uk: {
    brand: "Wholesale & Retail", tagline: "B2B дошка запитів на закупівлю.",
    subtagline: "Покупці публікують потреби. Продавці відповідають пропозиціями.",
    nav_home: "Дошка", nav_post: "Опублікувати запит",
    filter_all: "Усі категорії", search_placeholder: "Пошук запитів…",
    empty_title: "Ще немає запитів", empty_desc: "Будьте першим. Постачальники відповідатимуть.",
    empty_cta: "Опублікувати перший",
    request_by: "Опублікував", quantity: "Кількість", budget: "Бюджет", deadline: "Термін", category: "Категорія",
    view_details: "Переглянути й запропонувати", back_to_board: "До дошки", offers_count: "пропозицій",
    no_offers: "Ще немає пропозицій — будьте першим.", add_offer: "Додати пропозицію",
    form_new_request: "Новий запит на закупівлю",
    form_new_request_desc: "Опишіть, що хочете купити. Постачальники відповідатимуть з ціною та наявністю.",
    title_label: "Назва", title_placeholder: "напр. Червоні олівці, виробництво 80 000",
    description_label: "Опис", description_placeholder: "Специфікації, матеріали, якість…",
    category_label: "Категорія", category_placeholder: "Оберіть категорію",
    quantity_label: "Кількість", budget_label: "Бюджет", budget_placeholder: "напр. 500$ або відкритий",
    deadline_label: "Потрібно до", buyer_name_label: "Ваше ім'я / компанія", buyer_name_placeholder: "напр. Acme Studio",
    submit_request: "Опублікувати",
    seller_name: "Компанія / ім'я", seller_name_placeholder: "напр. Pencils R Us",
    price_label: "Ціна", price_placeholder: "напр. 450$ за 10 000 шт.",
    availability_label: "Наявність", availability_placeholder: "напр. В наявності, відправка 2 дні",
    offer_description_label: "Деталі", offer_description_placeholder: "Продукт, MOQ, умови…",
    contact_label: "Як зв'язатися", contact_placeholder: "email або телефон",
    submit_offer: "Надіслати пропозицію", uploading: "Завантаження…", posting: "Публікація…",
    posted: "Запит опубліковано", offer_posted: "Пропозиція надіслана",
    error_upload: "Помилка завантаження", error_submit: "Щось пішло не так",
    hero_kicker: "Дошка запитів", hero_line_1: "Опублікуйте що вам потрібно.", hero_line_2: "Нехай постачальники прийдуть.",
    stat_requests: "відкриті запити", stat_offers: "пропозиції", filters: "Фільтри", live: "У прямому",
    media_add: "Клацніть або перетягніть фото й відео",
    media_hint: "Кілька файлів · Зображення (макс 10МБ) · Відео (макс 100МБ)",
    photo_label: "Фото та відео (опційно)",
    email_label: "Email", email_placeholder: "ви@компанія.ua",
    phone_label: "Телефон", phone_placeholder: "+380 50 000 00 00",
    contact_tab: "Контакт", details_tab: "Деталі", offers_tab: "Пропозиції", ratings_tab: "Оцінки",
    leave_rating: "Залишити оцінку", your_name: "Ваше ім'я", your_name_placeholder: "напр. Іван П.",
    comment_optional: "Коментар (опційно)", comment_placeholder: "Поділіться досвідом…",
    submit_rating: "Надіслати оцінку", rating_posted: "Оцінка надіслана",
    reviews: "відгуки", no_ratings: "Ще немає оцінок — будьте першим.",
    language: "Мова",
  },
  ar: {
    brand: "Wholesale & Retail", tagline: "لوحة طلبات الشراء B2B.",
    subtagline: "المشترون ينشرون احتياجاتهم. البائعون يردون بعروض الأسعار.",
    nav_home: "اللوحة", nav_post: "نشر طلب",
    filter_all: "كل الفئات", search_placeholder: "ابحث عن طلبات…",
    empty_title: "لا توجد طلبات بعد", empty_desc: "كن أول من ينشر. سيرد الموردون بعروض.",
    empty_cta: "انشر أول طلب",
    request_by: "نشر بواسطة", quantity: "الكمية", budget: "الميزانية", deadline: "الموعد", category: "الفئة",
    view_details: "عرض واقتراح", back_to_board: "عودة للوحة", offers_count: "عروض البائعين",
    no_offers: "لا توجد عروض — كن أول مورد.", add_offer: "أضف عرضك",
    form_new_request: "طلب شراء جديد",
    form_new_request_desc: "صف ما تريد شراءه. سيرد الموردون بالأسعار والتوفر.",
    title_label: "العنوان", title_placeholder: "مثلاً أقلام حمراء، إنتاج 80,000",
    description_label: "الوصف", description_placeholder: "المواصفات، المواد، الجودة…",
    category_label: "الفئة", category_placeholder: "اختر الفئة",
    quantity_label: "الكمية", budget_label: "الميزانية", budget_placeholder: "مثلاً 500$ أو مفتوح",
    deadline_label: "مطلوب بحلول", buyer_name_label: "اسمك / شركتك", buyer_name_placeholder: "مثلاً Acme Studio",
    submit_request: "نشر الطلب",
    seller_name: "الشركة / الاسم", seller_name_placeholder: "مثلاً Pencils R Us",
    price_label: "السعر المعروض", price_placeholder: "مثلاً 450$ لـ 10,000 وحدة",
    availability_label: "التوفر", availability_placeholder: "مثلاً متوفر، شحن خلال يومين",
    offer_description_label: "التفاصيل", offer_description_placeholder: "المنتج، الحد الأدنى، الشروط…",
    contact_label: "طريقة التواصل", contact_placeholder: "بريد أو هاتف",
    submit_offer: "إرسال العرض", uploading: "جارٍ الرفع…", posting: "جارٍ النشر…",
    posted: "تم نشر الطلب", offer_posted: "تم إرسال العرض",
    error_upload: "فشل الرفع", error_submit: "حدث خطأ ما",
    hero_kicker: "لوحة الطلبات", hero_line_1: "انشر ما تحتاجه.", hero_line_2: "دع الموردين يأتون إليك.",
    stat_requests: "طلبات مفتوحة", stat_offers: "عروض", filters: "فلاتر", live: "مباشر",
    media_add: "اضغط أو اسحب الصور والفيديوهات",
    media_hint: "ملفات متعددة · صور (حد أقصى 10MB) · فيديو (حد أقصى 100MB)",
    photo_label: "صور وفيديوهات (اختياري)",
    email_label: "البريد", email_placeholder: "you@company.com",
    phone_label: "الهاتف", phone_placeholder: "+971 50 000 0000",
    contact_tab: "التواصل", details_tab: "التفاصيل", offers_tab: "العروض", ratings_tab: "التقييمات",
    leave_rating: "أضف تقييمًا", your_name: "اسمك", your_name_placeholder: "مثلاً أحمد م.",
    comment_optional: "تعليق (اختياري)", comment_placeholder: "شارك تجربتك…",
    submit_rating: "إرسال التقييم", rating_posted: "تم إرسال التقييم",
    reviews: "مراجعات", no_ratings: "لا توجد تقييمات — كن أول من يقيم.",
    language: "اللغة",
  },
  hi: {
    brand: "Wholesale & Retail", tagline: "B2B खरीद-अनुरोध बोर्ड।",
    subtagline: "खरीदार ज़रूरतें पोस्ट करते हैं। विक्रेता कोटेशन के साथ जवाब देते हैं।",
    nav_home: "बोर्ड", nav_post: "अनुरोध पोस्ट करें",
    filter_all: "सभी श्रेणियाँ", search_placeholder: "अनुरोध खोजें…",
    empty_title: "अभी कोई अनुरोध नहीं", empty_desc: "पहले पोस्ट करें। आपूर्तिकर्ता कोटेशन से जवाब देंगे।",
    empty_cta: "पहला अनुरोध पोस्ट करें",
    request_by: "पोस्ट किया", quantity: "मात्रा", budget: "बजट", deadline: "समय-सीमा", category: "श्रेणी",
    view_details: "देखें और ऑफर दें", back_to_board: "बोर्ड पर वापस", offers_count: "विक्रेता ऑफर",
    no_offers: "अभी कोई ऑफर नहीं — पहले जवाब दें।", add_offer: "ऑफर जोड़ें",
    form_new_request: "नया खरीद अनुरोध",
    form_new_request_desc: "बताएँ क्या खरीदना है। आपूर्तिकर्ता कीमत और उपलब्धता के साथ जवाब देंगे।",
    title_label: "शीर्षक", title_placeholder: "जैसे लाल पेंसिल, उत्पादन 80,000",
    description_label: "विवरण", description_placeholder: "स्पेसिफिकेशन, सामग्री, गुणवत्ता…",
    category_label: "श्रेणी", category_placeholder: "श्रेणी चुनें",
    quantity_label: "मात्रा", budget_label: "बजट", budget_placeholder: "जैसे ₹500 या खुला",
    deadline_label: "कब तक चाहिए", buyer_name_label: "आपका नाम / कंपनी", buyer_name_placeholder: "जैसे Acme Studio",
    submit_request: "पोस्ट करें",
    seller_name: "कंपनी / नाम", seller_name_placeholder: "जैसे Pencils R Us",
    price_label: "प्रस्तावित कीमत", price_placeholder: "जैसे 10,000 इकाई के लिए ₹450",
    availability_label: "उपलब्धता", availability_placeholder: "जैसे स्टॉक में, 2 दिन में शिप",
    offer_description_label: "विवरण", offer_description_placeholder: "उत्पाद, MOQ, शर्तें…",
    contact_label: "कैसे संपर्क करें", contact_placeholder: "ईमेल या फ़ोन",
    submit_offer: "ऑफर भेजें", uploading: "अपलोड हो रहा है…", posting: "पोस्ट हो रहा है…",
    posted: "अनुरोध पोस्ट हुआ", offer_posted: "ऑफर भेजा गया",
    error_upload: "अपलोड विफल", error_submit: "कुछ गड़बड़ हुआ",
    hero_kicker: "अनुरोध बोर्ड", hero_line_1: "जो चाहिए वो पोस्ट करें।", hero_line_2: "आपूर्तिकर्ता आपके पास आएँगे।",
    stat_requests: "खुले अनुरोध", stat_offers: "ऑफर", filters: "फ़िल्टर", live: "लाइव",
    media_add: "फ़ोटो और वीडियो क्लिक करें या खींचें",
    media_hint: "कई फ़ाइलें · छवियाँ (अधिकतम 10MB) · वीडियो (अधिकतम 100MB)",
    photo_label: "फ़ोटो और वीडियो (वैकल्पिक)",
    email_label: "ईमेल", email_placeholder: "you@company.com",
    phone_label: "फ़ोन", phone_placeholder: "+91 90000 00000",
    contact_tab: "संपर्क", details_tab: "विवरण", offers_tab: "ऑफर", ratings_tab: "रेटिंग",
    leave_rating: "रेटिंग दें", your_name: "आपका नाम", your_name_placeholder: "जैसे राहुल K.",
    comment_optional: "टिप्पणी (वैकल्पिक)", comment_placeholder: "अनुभव साझा करें…",
    submit_rating: "रेटिंग भेजें", rating_posted: "रेटिंग भेजी गई",
    reviews: "समीक्षाएँ", no_ratings: "कोई रेटिंग नहीं — पहले रेट करें।",
    language: "भाषा",
  },
  zh: {
    brand: "Wholesale & Retail", tagline: "B2B 采购需求板。",
    subtagline: "买家发布需求。卖家用报价回复。",
    nav_home: "看板", nav_post: "发布需求",
    filter_all: "所有类别", search_placeholder: "搜索需求…",
    empty_title: "暂无需求", empty_desc: "成为第一个发布者。供应商会用报价回复。",
    empty_cta: "发布第一个",
    request_by: "发布者", quantity: "数量", budget: "预算", deadline: "截止日期", category: "类别",
    view_details: "查看并报价", back_to_board: "返回看板", offers_count: "卖家报价",
    no_offers: "暂无报价 — 成为第一个。", add_offer: "添加报价",
    form_new_request: "新采购需求",
    form_new_request_desc: "描述你要采购的物品。供应商将回复价格和库存情况。",
    title_label: "标题", title_placeholder: "例如：红色铅笔,生产 80,000 支",
    description_label: "描述", description_placeholder: "规格、材料、质量…",
    category_label: "类别", category_placeholder: "选择类别",
    quantity_label: "数量", budget_label: "预算", budget_placeholder: "例如 ¥500 或开放",
    deadline_label: "需要日期", buyer_name_label: "你的名字 / 公司", buyer_name_placeholder: "例如 Acme Studio",
    submit_request: "发布需求",
    seller_name: "公司 / 姓名", seller_name_placeholder: "例如 Pencils R Us",
    price_label: "报价", price_placeholder: "例如 10,000 件 ¥450",
    availability_label: "库存", availability_placeholder: "例如 现货,2 天发货",
    offer_description_label: "详情", offer_description_placeholder: "产品、MOQ、条款…",
    contact_label: "联系方式", contact_placeholder: "邮箱或电话",
    submit_offer: "提交报价", uploading: "上传中…", posting: "发布中…",
    posted: "需求已发布", offer_posted: "报价已提交",
    error_upload: "上传失败", error_submit: "出错了",
    hero_kicker: "需求板", hero_line_1: "发布你的需求。", hero_line_2: "让供应商主动找你。",
    stat_requests: "开放需求", stat_offers: "报价", filters: "筛选", live: "实时",
    media_add: "点击或拖入照片和视频",
    media_hint: "多个文件 · 图片(最大 10MB)· 视频(最大 100MB)",
    photo_label: "照片和视频(可选)",
    email_label: "邮箱", email_placeholder: "you@company.com",
    phone_label: "电话", phone_placeholder: "+86 138 0000 0000",
    contact_tab: "联系", details_tab: "详情", offers_tab: "报价", ratings_tab: "评价",
    leave_rating: "留下评价", your_name: "你的名字", your_name_placeholder: "例如 王 L.",
    comment_optional: "评论(可选)", comment_placeholder: "分享你的体验…",
    submit_rating: "提交评价", rating_posted: "评价已提交",
    reviews: "评论", no_ratings: "暂无评价 — 成为第一个。",
    language: "语言",
  },
  ja: {
    brand: "Wholesale & Retail", tagline: "B2B購入依頼ボード。",
    subtagline: "買い手はニーズを投稿。売り手は見積もりで返信。",
    nav_home: "ボード", nav_post: "依頼を投稿",
    filter_all: "全カテゴリ", search_placeholder: "依頼を検索…",
    empty_title: "まだ依頼がありません", empty_desc: "最初に投稿しましょう。サプライヤーが見積もりで返信します。",
    empty_cta: "最初の依頼を投稿",
    request_by: "投稿者", quantity: "数量", budget: "予算", deadline: "期限", category: "カテゴリ",
    view_details: "詳細と提案", back_to_board: "ボードへ戻る", offers_count: "件の提案",
    no_offers: "まだ提案がありません — 最初になりましょう。", add_offer: "提案を追加",
    form_new_request: "新規購入依頼",
    form_new_request_desc: "何を購入したいか説明してください。サプライヤーが価格と在庫で返信します。",
    title_label: "タイトル", title_placeholder: "例: 赤鉛筆、生産 80,000 本",
    description_label: "説明", description_placeholder: "仕様、材料、品質…",
    category_label: "カテゴリ", category_placeholder: "カテゴリを選択",
    quantity_label: "数量", budget_label: "予算", budget_placeholder: "例: ¥500 またはオープン",
    deadline_label: "必要期日", buyer_name_label: "お名前 / 会社", buyer_name_placeholder: "例: Acme Studio",
    submit_request: "投稿",
    seller_name: "会社 / お名前", seller_name_placeholder: "例: Pencils R Us",
    price_label: "提示価格", price_placeholder: "例: 10,000 個で ¥450",
    availability_label: "在庫状況", availability_placeholder: "例: 在庫あり、2 日で出荷",
    offer_description_label: "詳細", offer_description_placeholder: "製品、MOQ、条件…",
    contact_label: "連絡方法", contact_placeholder: "メールまたは電話",
    submit_offer: "提案を送信", uploading: "アップロード中…", posting: "投稿中…",
    posted: "依頼を投稿しました", offer_posted: "提案を送信しました",
    error_upload: "アップロード失敗", error_submit: "エラーが発生しました",
    hero_kicker: "依頼ボード", hero_line_1: "必要なものを投稿。", hero_line_2: "サプライヤーに来てもらおう。",
    stat_requests: "公開依頼", stat_offers: "提案", filters: "フィルタ", live: "ライブ",
    media_add: "写真と動画をクリックまたはドラッグ",
    media_hint: "複数ファイル · 画像(最大 10MB)· 動画(最大 100MB)",
    photo_label: "写真と動画(任意)",
    email_label: "メール", email_placeholder: "you@company.com",
    phone_label: "電話", phone_placeholder: "+81 90 0000 0000",
    contact_tab: "連絡", details_tab: "詳細", offers_tab: "提案", ratings_tab: "評価",
    leave_rating: "評価する", your_name: "お名前", your_name_placeholder: "例: 田中 K.",
    comment_optional: "コメント(任意)", comment_placeholder: "経験を共有…",
    submit_rating: "評価を送信", rating_posted: "評価を送信しました",
    reviews: "レビュー", no_ratings: "まだ評価がありません — 最初になりましょう。",
    language: "言語",
  },
  ko: {
    brand: "Wholesale & Retail", tagline: "B2B 구매 요청 보드.",
    subtagline: "구매자가 필요한 것을 게시. 판매자가 견적으로 응답.",
    nav_home: "보드", nav_post: "요청 게시",
    filter_all: "모든 카테고리", search_placeholder: "요청 검색…",
    empty_title: "아직 요청 없음", empty_desc: "먼저 게시해보세요. 공급업체가 견적으로 응답합니다.",
    empty_cta: "첫 요청 게시",
    request_by: "게시자", quantity: "수량", budget: "예산", deadline: "마감일", category: "카테고리",
    view_details: "보기 및 제안", back_to_board: "보드로", offers_count: "판매자 제안",
    no_offers: "아직 제안 없음 — 먼저 응답하세요.", add_offer: "제안 추가",
    form_new_request: "새 구매 요청",
    form_new_request_desc: "구매하려는 것을 설명하세요. 공급업체가 가격과 재고로 응답합니다.",
    title_label: "제목", title_placeholder: "예: 빨간 연필, 생산 80,000개",
    description_label: "설명", description_placeholder: "사양, 재료, 품질…",
    category_label: "카테고리", category_placeholder: "카테고리 선택",
    quantity_label: "수량", budget_label: "예산", budget_placeholder: "예: ₩500,000 또는 오픈",
    deadline_label: "필요 기한", buyer_name_label: "이름 / 회사", buyer_name_placeholder: "예: Acme Studio",
    submit_request: "게시",
    seller_name: "회사 / 이름", seller_name_placeholder: "예: Pencils R Us",
    price_label: "제안 가격", price_placeholder: "예: 10,000개에 ₩450,000",
    availability_label: "재고", availability_placeholder: "예: 재고 있음, 2일 내 배송",
    offer_description_label: "세부사항", offer_description_placeholder: "제품, MOQ, 조건…",
    contact_label: "연락 방법", contact_placeholder: "이메일 또는 전화",
    submit_offer: "제안 보내기", uploading: "업로드 중…", posting: "게시 중…",
    posted: "요청 게시됨", offer_posted: "제안 전송됨",
    error_upload: "업로드 실패", error_submit: "오류가 발생했습니다",
    hero_kicker: "요청 보드", hero_line_1: "필요한 것을 게시.", hero_line_2: "공급업체가 찾아오게.",
    stat_requests: "열린 요청", stat_offers: "제안", filters: "필터", live: "실시간",
    media_add: "사진과 동영상 클릭 또는 드래그",
    media_hint: "여러 파일 · 이미지(최대 10MB)· 동영상(최대 100MB)",
    photo_label: "사진 및 동영상 (선택)",
    email_label: "이메일", email_placeholder: "you@company.com",
    phone_label: "전화", phone_placeholder: "+82 10 0000 0000",
    contact_tab: "연락처", details_tab: "세부사항", offers_tab: "제안", ratings_tab: "평가",
    leave_rating: "평가 남기기", your_name: "이름", your_name_placeholder: "예: 김 J.",
    comment_optional: "댓글 (선택)", comment_placeholder: "경험을 공유…",
    submit_rating: "평가 보내기", rating_posted: "평가 전송됨",
    reviews: "리뷰", no_ratings: "평가 없음 — 먼저 평가하세요.",
    language: "언어",
  },
  id: {
    brand: "Wholesale & Retail", tagline: "Papan permintaan pembelian B2B.",
    subtagline: "Pembeli memposting kebutuhan. Penjual membalas dengan penawaran.",
    nav_home: "Papan", nav_post: "Buat permintaan",
    filter_all: "Semua kategori", search_placeholder: "Cari permintaan…",
    empty_title: "Belum ada permintaan", empty_desc: "Jadilah yang pertama. Pemasok akan membalas dengan penawaran.",
    empty_cta: "Buat permintaan pertama",
    request_by: "Diposting oleh", quantity: "Jumlah", budget: "Anggaran", deadline: "Batas waktu", category: "Kategori",
    view_details: "Lihat & tawarkan", back_to_board: "Kembali", offers_count: "penawaran penjual",
    no_offers: "Belum ada penawaran — jadilah yang pertama.", add_offer: "Tambah penawaran",
    form_new_request: "Permintaan pembelian baru",
    form_new_request_desc: "Jelaskan apa yang ingin dibeli. Pemasok akan membalas dengan harga dan ketersediaan.",
    title_label: "Judul", title_placeholder: "cth. Pensil merah, produksi 80.000",
    description_label: "Deskripsi", description_placeholder: "Spesifikasi, bahan, kualitas…",
    category_label: "Kategori", category_placeholder: "Pilih kategori",
    quantity_label: "Jumlah", budget_label: "Anggaran", budget_placeholder: "cth. Rp500.000 atau terbuka",
    deadline_label: "Dibutuhkan", buyer_name_label: "Nama / perusahaan", buyer_name_placeholder: "cth. Acme Studio",
    submit_request: "Publikasikan",
    seller_name: "Perusahaan / nama", seller_name_placeholder: "cth. Pencils R Us",
    price_label: "Harga ditawarkan", price_placeholder: "cth. Rp450.000 untuk 10.000 unit",
    availability_label: "Ketersediaan", availability_placeholder: "cth. Ready, kirim 2 hari",
    offer_description_label: "Detail", offer_description_placeholder: "Produk, MOQ, syarat…",
    contact_label: "Cara menghubungi", contact_placeholder: "email atau telepon",
    submit_offer: "Kirim penawaran", uploading: "Mengunggah…", posting: "Mempublikasikan…",
    posted: "Permintaan dipublikasikan", offer_posted: "Penawaran dikirim",
    error_upload: "Unggah gagal", error_submit: "Terjadi kesalahan",
    hero_kicker: "Papan permintaan", hero_line_1: "Posting apa yang Anda butuhkan.", hero_line_2: "Biarkan pemasok datang.",
    stat_requests: "permintaan terbuka", stat_offers: "penawaran", filters: "Filter", live: "Langsung",
    media_add: "Klik atau seret foto & video",
    media_hint: "Beberapa file · Gambar (maks 10MB) · Video (maks 100MB)",
    photo_label: "Foto & video (opsional)",
    email_label: "Email", email_placeholder: "kamu@perusahaan.com",
    phone_label: "Telepon", phone_placeholder: "+62 812 0000 0000",
    contact_tab: "Kontak", details_tab: "Detail", offers_tab: "Penawaran", ratings_tab: "Peringkat",
    leave_rating: "Beri peringkat", your_name: "Nama Anda", your_name_placeholder: "cth. Budi S.",
    comment_optional: "Komentar (opsional)", comment_placeholder: "Bagikan pengalaman…",
    submit_rating: "Kirim peringkat", rating_posted: "Peringkat dikirim",
    reviews: "ulasan", no_ratings: "Belum ada peringkat — jadilah yang pertama.",
    language: "Bahasa",
  },
  vi: {
    brand: "Wholesale & Retail", tagline: "Bảng yêu cầu mua B2B.",
    subtagline: "Người mua đăng nhu cầu. Người bán trả lời với báo giá.",
    nav_home: "Bảng", nav_post: "Đăng yêu cầu",
    filter_all: "Tất cả danh mục", search_placeholder: "Tìm yêu cầu…",
    empty_title: "Chưa có yêu cầu", empty_desc: "Hãy là người đầu tiên. Nhà cung cấp sẽ báo giá.",
    empty_cta: "Đăng yêu cầu đầu",
    request_by: "Đăng bởi", quantity: "Số lượng", budget: "Ngân sách", deadline: "Thời hạn", category: "Danh mục",
    view_details: "Xem & báo giá", back_to_board: "Quay lại", offers_count: "báo giá",
    no_offers: "Chưa có báo giá — hãy là người đầu.", add_offer: "Thêm báo giá",
    form_new_request: "Yêu cầu mua mới",
    form_new_request_desc: "Mô tả bạn muốn mua gì. Nhà cung cấp sẽ trả lời với giá và tồn kho.",
    title_label: "Tiêu đề", title_placeholder: "ví dụ Bút chì đỏ, sản xuất 80.000",
    description_label: "Mô tả", description_placeholder: "Thông số, vật liệu, chất lượng…",
    category_label: "Danh mục", category_placeholder: "Chọn danh mục",
    quantity_label: "Số lượng", budget_label: "Ngân sách", budget_placeholder: "ví dụ 500.000₫ hoặc mở",
    deadline_label: "Cần vào", buyer_name_label: "Tên / công ty", buyer_name_placeholder: "ví dụ Acme Studio",
    submit_request: "Đăng",
    seller_name: "Công ty / tên", seller_name_placeholder: "ví dụ Pencils R Us",
    price_label: "Giá đề nghị", price_placeholder: "ví dụ 450.000₫ cho 10.000 sản phẩm",
    availability_label: "Tình trạng", availability_placeholder: "ví dụ Có sẵn, giao 2 ngày",
    offer_description_label: "Chi tiết", offer_description_placeholder: "Sản phẩm, MOQ, điều kiện…",
    contact_label: "Cách liên hệ", contact_placeholder: "email hoặc điện thoại",
    submit_offer: "Gửi báo giá", uploading: "Đang tải…", posting: "Đang đăng…",
    posted: "Yêu cầu đã đăng", offer_posted: "Báo giá đã gửi",
    error_upload: "Tải lên thất bại", error_submit: "Có lỗi xảy ra",
    hero_kicker: "Bảng yêu cầu", hero_line_1: "Đăng thứ bạn cần.", hero_line_2: "Để nhà cung cấp đến với bạn.",
    stat_requests: "yêu cầu mở", stat_offers: "báo giá", filters: "Bộ lọc", live: "Trực tiếp",
    media_add: "Bấm hoặc kéo ảnh & video",
    media_hint: "Nhiều tệp · Ảnh (tối đa 10MB) · Video (tối đa 100MB)",
    photo_label: "Ảnh & video (tùy chọn)",
    email_label: "Email", email_placeholder: "ban@congty.com",
    phone_label: "Điện thoại", phone_placeholder: "+84 90 000 0000",
    contact_tab: "Liên hệ", details_tab: "Chi tiết", offers_tab: "Báo giá", ratings_tab: "Đánh giá",
    leave_rating: "Để lại đánh giá", your_name: "Tên bạn", your_name_placeholder: "ví dụ Nguyễn V.",
    comment_optional: "Nhận xét (tùy chọn)", comment_placeholder: "Chia sẻ trải nghiệm…",
    submit_rating: "Gửi đánh giá", rating_posted: "Đánh giá đã gửi",
    reviews: "đánh giá", no_ratings: "Chưa có đánh giá — hãy là người đầu.",
    language: "Ngôn ngữ",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("lang");
    return saved && translations[saved] ? saved : "en";
  });

  useEffect(() => {
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGS.has(lang) ? "rtl" : "ltr";
  }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.en;
    const fallback = translations.en;
    return (key) => dict[key] || fallback[key] || key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
}
